'use client';

/**
 * The file half of the free tool -> widget bridge.
 *
 * Text travels in localStorage (see toolBridge.ts). Images cannot: localStorage
 * holds strings only, and a file turned into a base64 string would blow the
 * ~5MB origin quota on a single phone photo. IndexedDB stores real Blobs and
 * has a far larger, browser-managed quota, so the actual files ride across the
 * navigation here while the board rides across there.
 *
 * Nothing is uploaded at this stage. The files sit in the visitor's own browser
 * and only reach our servers if they sign up and save a widget, which is what
 * keeps a visitor who never converts free for us.
 *
 * Scopes:
 *   draft:<toolSlug>   what the tool is holding right now, so a refresh can put
 *                      the pictures back rather than only their labels
 *   bridge:<key>       a snapshot copied at click time, read once by the widget
 */

const DB_NAME = 'cn-tool-bridge';
const DB_VERSION = 1;
const STORE = 'files';

/** A bridge snapshot is a single navigation. A draft is worth keeping a week. */
export const BRIDGE_FILES_TTL_MS = 30 * 60 * 1000;
export const DRAFT_FILES_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export interface IBridgeFile {
  /** `${scope}::${itemId}` - the primary key. */
  id: string;
  scope: string;
  itemId: string;
  name: string;
  type: string;
  blob: Blob;
  createdAt: number;
}

export const draftScope = (toolSlug: string) => `draft:${toolSlug}`;
export const bridgeScope = (key: string) => `bridge:${key}`;

/**
 * Every call is guarded and resolves rather than throwing. IndexedDB is absent
 * or blocked in private windows, hardened privacy settings and some embedded
 * browsers, and a missing picture must never break the tool or the editor.
 */
function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      if (typeof window === 'undefined' || !window.indexedDB) {
        resolve(null);
        return;
      }

      const request = window.indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE)) {
          const store = db.createObjectStore(STORE, { keyPath: 'id' });
          store.createIndex('scope', 'scope', { unique: false });
          store.createIndex('createdAt', 'createdAt', { unique: false });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    } catch (err) {
      resolve(null);
    }
  });
}

function done(tx: IDBTransaction): Promise<boolean> {
  return new Promise((resolve) => {
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => resolve(false);
    tx.onabort = () => resolve(false);
  });
}

/** Replaces everything held for a scope. */
export async function putScopeFiles(
  scope: string,
  entries: { itemId: string; file: File | Blob; name?: string }[]
): Promise<boolean> {
  const db = await openDb();
  if (!db) return false;

  try {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    const index = store.index('scope');

    // Clear the scope first, so removing an image in the tool removes its file.
    const cursorReq = index.openKeyCursor(IDBKeyRange.only(scope));
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result;
      if (cursor) {
        store.delete(cursor.primaryKey);
        cursor.continue();
      } else {
        entries.forEach(({ itemId, file, name }) => {
          const record: IBridgeFile = {
            id: `${scope}::${itemId}`,
            scope,
            itemId,
            name: name || (file as File).name || 'image',
            type: file.type || 'image/png',
            blob: file,
            createdAt: Date.now(),
          };
          store.put(record);
        });
      }
    };

    const ok = await done(tx);
    db.close();
    return ok;
  } catch (err) {
    try { db.close(); } catch (e) {}
    return false;
  }
}

export async function getScopeFiles(scope: string): Promise<IBridgeFile[]> {
  const db = await openDb();
  if (!db) return [];

  try {
    const tx = db.transaction(STORE, 'readonly');
    const index = tx.objectStore(STORE).index('scope');
    const request = index.getAll(IDBKeyRange.only(scope));

    const records: IBridgeFile[] = await new Promise((resolve) => {
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => resolve([]);
    });

    db.close();
    return records;
  } catch (err) {
    try { db.close(); } catch (e) {}
    return [];
  }
}

export async function deleteScopeFiles(scope: string): Promise<boolean> {
  return putScopeFiles(scope, []);
}

/** Copies a scope to another, which is how a click snapshots the draft. */
export async function copyScopeFiles(from: string, to: string): Promise<number> {
  const records = await getScopeFiles(from);
  if (!records.length) return 0;

  const ok = await putScopeFiles(
    to,
    records.map((r) => ({ itemId: r.itemId, file: r.blob, name: r.name }))
  );
  return ok ? records.length : 0;
}

/**
 * Housekeeping. Bridge snapshots outlive their trip by half an hour at most;
 * drafts by a week. Runs on write, so an abandoned visit cleans up after itself
 * rather than sitting in someone's browser forever.
 */
export async function sweepOldFiles(): Promise<void> {
  const db = await openDb();
  if (!db) return;

  try {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    const request = store.openCursor();
    const now = Date.now();

    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) return;

      const record = cursor.value as IBridgeFile;
      const ttl = record.scope?.startsWith('bridge:')
        ? BRIDGE_FILES_TTL_MS
        : DRAFT_FILES_TTL_MS;

      if (typeof record.createdAt !== 'number' || now - record.createdAt > ttl) {
        cursor.delete();
      }
      cursor.continue();
    };

    await done(tx);
    db.close();
  } catch (err) {
    try { db.close(); } catch (e) {}
  }
}
